import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';
import { UI_TEXT } from '@/shared/languages';
import { Button } from '@/shared/ui';

/**
 * React 렌더링 중 발생한 에러를 catch 하고 fallback UI 표시.
 *
 * 현재 단계: Phase A — UI 가시화만. Sentry 리포팅·`AppError` hierarchy·RFC 7807 파싱 등은 보류
 * (docs/open-issues.md §2 참조).
 *
 * 위치: `app/providers/` 의 `AppProvider` 최외곽에 합류해 전 트리 렌더 에러를 잡는다.
 */

interface ErrorBoundaryProps extends PropsWithChildren {
  /** 커스텀 fallback. 미지정 시 기본 UI. */
  fallback?: (error: Error, retry: () => void) => ReactNode;
  /** 에러 발생 콜백 (로깅·리포팅 hook). 기본 `console.error`. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    } else {
      // Sentry 미통합 단계 fallback. Phase B 통합 시 `reportToSentry` 로 교체.
      console.error('[ErrorBoundary]', error, errorInfo);
    }
  }

  retry = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.retry);
      return <DefaultErrorFallback error={error} retry={this.retry} />;
    }
    return this.props.children;
  }
}

function DefaultErrorFallback({ error, retry }: { error: Error; retry: () => void }) {
  return (
    <div
      role="alert"
      className="flex min-h-dvh flex-col items-center justify-center bg-background px-6 text-center"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div className="w-full max-w-sm">
        <h1 className="text-lg font-bold">{UI_TEXT.ERROR_BOUNDARY_TITLE}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{UI_TEXT.ERROR_BOUNDARY_DESCRIPTION}</p>
        {import.meta.env.DEV && (
          <pre className="mt-4 overflow-x-auto rounded-md border border-border bg-muted p-3 text-left text-xs text-muted-foreground">
            {error.message}
          </pre>
        )}
        <Button onClick={retry} className="mt-6">
          {UI_TEXT.COMMON_RETRY}
        </Button>
      </div>
    </div>
  );
}
